import React, { useState, useRef, ReactNode } from 'react';
import { TooltipTrigger, Tooltip } from '@adobe/react-spectrum';

interface CommonTooltipProps {
  children: ReactNode;
  tooltipContent: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number; // Delay in milliseconds before showing tooltip
  testId?: string;
}

/**
 * CommonTooltip - A reusable tooltip wrapper with consistent timing behavior
 *
 * Features:
 * - Configurable delay before showing tooltip (default: 1000ms)
 * - Automatic cleanup on unmount
 * - Consistent behavior across all milestone grid tooltips
 *
 * Usage:
 * <CommonTooltip tooltipContent="Tooltip text">
 *   <ActionButton>Button content</ActionButton>
 * </CommonTooltip>
 */
export const CommonTooltip: React.FC<CommonTooltipProps> = ({
  children,
  tooltipContent,
  placement = 'top',
  delay = 1000,
  testId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    tooltipTimerRef.current = setTimeout(() => {
      setIsOpen(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    setIsOpen(false);
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  return (
    <TooltipTrigger
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) setIsOpen(false);
      }}
      placement={placement}
    >
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ display: 'contents' }}
        data-testid={testId}
      >
        {children}
      </div>
      <Tooltip>{tooltipContent}</Tooltip>
    </TooltipTrigger>
  );
};
